export type ClassroomAiPhaseId = 'observe' | 'analyze' | 'create' | 'review';

export interface ClassroomAiPhase {
  id: ClassroomAiPhaseId;
  name: string;
  summary: string;
  strategies: string[];
}

export interface IdeaItem {
  index: number;
  question: string;
  keyword: string;
}

export interface IdeaBatch {
  type: 'ideas';
  label: string;
  tag: string;
  items: IdeaItem[];
  time?: string;
  sourceType?: 'deepen';
  sourceBatchIndex?: number;
}

export interface LoadingBatchState {
  batchIndex: number;
  type: 'refresh' | 'deepen';
  baseQuestion?: string;
}

export interface DrawState {
  loading?: boolean;
  refreshing?: boolean;
  url?: string;
  ideaText?: string;
  error?: string;
}

export const CLASSROOM_AI_PHASES: ClassroomAiPhase[] = [
  {
    id: 'observe',
    name: '导入观察',
    summary: '通过提问拉起观察注意力，让学生先说、先看、先猜。',
    strategies: ['观察与表现', '学生开口', '轻提问'],
  },
  {
    id: 'analyze',
    name: '结构启发',
    summary: '把课题拆成可理解的结构与层次，让学生知道从哪下手。',
    strategies: ['骨架透视镜', '拆形体', '关系判断'],
  },
  {
    id: 'create',
    name: '创作推进',
    summary: '在正式创作中帮助教师做发散和卡点解锁。',
    strategies: ['创作与深化', '卡点解锁', '多解法'],
  },
  {
    id: 'review',
    name: '展示点评',
    summary: '帮助学生复盘思考路径和表达选择。',
    strategies: ['审美与内化', '复盘表达', '同伴互评'],
  },
];

export const CLASSROOM_AI_IDEA_MAP: Record<
  ClassroomAiPhaseId,
  { inspire: string[]; deeper: string[]; refresh: string[] }
> = {
  observe: {
    inspire: [
      '你第一眼最想让学生先看见哪一个特征？',
      '这只狐狸最有记忆点的地方是什么？',
      '如果现在先不画，你会先让大家说出它给人的第一感觉是什么？',
    ],
    deeper: [
      '为什么你会先看到这里，不是别处？',
      '如果它动起来，你觉得最先动的是哪里？',
      '你说它可爱，那是因为外形、动作，还是表情？',
    ],
    refresh: [
      '如果不说狐狸，你会先用什么词形容这个角色？',
      '它更像安静地站着，还是正准备跑起来？',
      '如果只能留一个最能代表它的特征，你会留哪里？',
    ],
  },
  analyze: {
    inspire: [
      '如果只保留关键结构，哪几条线最不能删？',
      '前后层次里，谁该更明确、谁该更轻？',
      '把狐狸拆成几个大形以后，你会先从哪一块起稿？',
    ],
    deeper: [
      '你为什么判断这里应该更靠前？',
      '如果这条线再倾斜一点，动态会不会更明显？',
      '你现在这根线是在交代外轮廓，还是在交代转折？',
    ],
    refresh: [
      '如果把狐狸拆成几个大形，你会先圈出哪几块？',
      '前景和后景如果只能保留一个更明显，你会先保留谁？',
      '你觉得学生现在最容易混掉的是比例、结构，还是空间关系？',
    ],
  },
  create: {
    inspire: [
      '现在补哪一个元素，会让画面更完整？',
      '如果想更有故事感，你会先改哪里？',
      '你最想让学生在哪个细节里开始做自己的表达？',
    ],
    deeper: [
      '你说想更丰富，那你准备增加什么，而且为什么是它？',
      '如果只允许再改一处，你会改哪里？',
      '这个元素加进去以后，它是帮主体更突出，还是让画面更乱？',
    ],
    refresh: [
      '除了狐狸本身，还有什么能让这张画更像在森林里？',
      '如果想让这张画更像你自己的故事，你会换掉哪个地方？',
      '现在最值得补的是主体细节，还是环境线索？',
    ],
  },
  review: {
    inspire: [
      '你最想让大家先看到画面的哪一处？',
      '如果再多加一个细节，哪里最值得补？',
      '你觉得这张画里，哪一部分最像你本来想表达的样子？',
    ],
    deeper: [
      '你为什么最满意这里，它解决了什么问题？',
      '如果再来一次，你最先会保留哪一个做法？',
      '你说这里好，那它到底好在结构、层次，还是故事感？',
    ],
    refresh: [
      '如果让同学一眼看出你的想法，你最希望他先看到哪里？',
      '这张画最像你心里想的那一版吗，还是还有一点差别？',
      '如果只说一个最想继续改进的地方，你会选哪里？',
    ],
  },
};

export function createEmptyBatchState(): Record<ClassroomAiPhaseId, IdeaBatch[]> {
  return {
    observe: [],
    analyze: [],
    create: [],
    review: [],
  };
}

export function createEmptyDrawState(): Record<ClassroomAiPhaseId, Record<string, DrawState>> {
  return {
    observe: {},
    analyze: {},
    create: {},
    review: {},
  };
}
